import {
  designTokens,
  Divider,
  Heading,
  Loader,
  YokuaruBody,
  YokuaruHeader
} from '@itandi/itandi-bb-ui';
import { useState } from 'react';
import { DealTypeFilter } from '~/components/common/home/DealTypeFilter';
import type { StatusCardItems } from '~/components/common/home/StatusCardList';
import { StatusCardList } from '~/components/common/home/StatusCardList';
import { VendorLayoutV2 } from '~/components/common/layouts/VendorLayoutV2';
import { useHomes } from '~/hooks/vendors/useHomes';
import {
  dealApplicationStatusQueryParam,
  dealStatusQueryParam
} from '~/models/DealDashboard';
import type { DealType } from '~/models/Deal';

export const Page: React.FC = () => {
  const [dealTypes, setDealTypes] = useState<DealType[]>([
    'restoration',
    'repair',
    'other'
  ]);
  const { data: dealDashboard } = useHomes().index({ dealTypes });
  if (dealDashboard === null) return <Loader />;
  const dealTypeCards = [
    {
      count: dealDashboard.dealTypeInfo.restorationCount,
      href: '/vendors/deals?deal_types[]=restoration',
      label: '原状回復',
      variant: 'default'
    },
    {
      count: dealDashboard.dealTypeInfo.repairCount,
      href: '/vendors/deals?deal_types[]=repair',
      label: '修繕',
      variant: 'default'
    },
    {
      count: dealDashboard.dealTypeInfo.otherCount,
      href: '/vendors/deals?deal_types[]=other',
      label: 'その他',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;

  const dealStatusCards = [
    {
      count: dealDashboard.dealStatusInfo.nowAcceptingCount,
      href: `/vendors/deals?${dealStatusQueryParam(['10'], dealTypes)}`,
      label: '業者選択中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.inEstimationCount,
      href: `/vendors/deals?${dealStatusQueryParam(['15'], dealTypes)}`,
      label: '見積作成中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.sendEstimateCount,
      href: `/vendors/deals?${dealStatusQueryParam(['40'], dealTypes)}`,
      label: '見積確認中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.inConstructionCount,
      href: `/vendors/deals?${dealStatusQueryParam(['45'], dealTypes)}`,
      label: '工事完了待ち',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.awaitingAcceptanceCount,
      href: `/vendors/deals?${dealStatusQueryParam(['90'], dealTypes)}`,
      label: '検収中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.finishedCount,
      href: `/vendors/deals?${dealStatusQueryParam(['95', '100'], dealTypes)}`,
      label: '完了',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;

  const dealApplicationStatusCards = [
    {
      count: dealDashboard.dealApplicationTypeInfo.requestedCount,
      href: `/vendors/deals?${dealApplicationStatusQueryParam(
        'requestEstimationByCompany',
        dealTypes
      )}`,
      label: '見積申請あり',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.requestCount,
      href: `/vendors/deals?${dealApplicationStatusQueryParam(
        'requestEstimation',
        dealTypes
      )}`,
      label: '見積申請中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.draftCount,
      href: `/vendors/deals?${dealApplicationStatusQueryParam(
        'draftEstimation',
        dealTypes
      )}`,
      label: '見積編集中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.waitingCancelCount,
      href: `/vendors/deals?${dealApplicationStatusQueryParam(
        'cancel',
        dealTypes
      )}`,
      label: '取下申請',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.acceptedCancelCount,
      href: `/vendors/deals?${dealStatusQueryParam(['901'], dealTypes)}`,
      label: '取下完了',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;
  return (
    <VendorLayoutV2>
      <YokuaruHeader heading={<Heading as="h1">ダッシュボード</Heading>} />
      <YokuaruBody>
        <div>
          <Heading as="h2" mb={designTokens['spacing/m']}>
            進行中の案件
          </Heading>
          <StatusCardList statusCards={dealTypeCards} />
        </div>
        <Divider color={designTokens['color/border/default']} />
        <DealTypeFilter
          dealDashboard={dealDashboard}
          dealTypes={dealTypes}
          setDealTypes={setDealTypes}
        />
        <div>
          <Heading as="h3" color="subtle" mb={designTokens['spacing/m']}>
            ステータス状況
          </Heading>
          <StatusCardList statusCards={dealStatusCards} />
        </div>
        <div>
          <Heading as="h3" color="subtle" mb={designTokens['spacing/m']}>
            申請状況
          </Heading>
          <StatusCardList statusCards={dealApplicationStatusCards} />
        </div>
      </YokuaruBody>
    </VendorLayoutV2>
  );
};
